import { clsx } from 'clsx'
import { useRef, useState, type ReactNode } from 'react'
import { Button } from './Button'
import { Icons } from './index'

export type UploadJob = { id: string; name: string; progress: number; error?: string }

type Props = {
  onFiles: (files: File[]) => void
  jobs?: UploadJob[]
  accept?: string
  multiple?: boolean
  disabled?: boolean
  hint?: string
  className?: string
}

/** Drop zone + file picker. Upload logic lives with the caller (needs the client id and storage path). */
export function Uploader({ onFiles, jobs = [], accept, multiple = true, disabled, hint, className }: Props) {
  const input = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  return (
    <div className={clsx('grid gap-3', className)}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => !disabled && input.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault()
            input.current?.click()
          }
        }}
        onDragOver={(e) => {
          e.preventDefault()
          if (!disabled) setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setOver(false)
          if (disabled) return
          const files = Array.from(e.dataTransfer.files)
          if (files.length) onFiles(multiple ? files : files.slice(0, 1))
        }}
        className={clsx(
          'grid cursor-pointer place-items-center gap-2 border border-dashed px-6 py-8 text-center transition-colors',
          over ? 'border-ink bg-gray-50' : 'border-gray-300 hover:border-ink',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <Icons.Plus size={16} className="text-dim" />
        {/* Phones and tablets can't drag files in, so they're only asked to tap. */}
        <p className="text-small text-ink">
          <span className="pointer-coarse:hidden">Drop files here, or click to choose</span>
          <span className="hidden pointer-coarse:inline">Tap to choose files</span>
        </p>
        {hint && <p className="text-[0.75rem] text-dim">{hint}</p>}
        <input
          ref={input}
          type="file"
          className="sr-only"
          accept={accept}
          multiple={multiple}
          onChange={(e) => {
            const files = Array.from(e.target.files ?? [])
            if (files.length) onFiles(files)
            e.target.value = ''
          }}
        />
      </div>
      {jobs.length > 0 && (
        <ul className="grid gap-2">
          {jobs.map((j) => (
            <li key={j.id} className="grid gap-1">
              <div className="flex items-center justify-between gap-3 text-small">
                <span className="truncate text-ink">{j.name}</span>
                <span className={clsx('text-[0.6875rem] uppercase tracking-eyebrow', j.error ? 'text-danger' : 'text-dim')}>{j.error ?? (j.progress >= 100 ? 'Done' : `${Math.round(j.progress)}%`)}</span>
              </div>
              <div role="progressbar" aria-valuenow={Math.round(j.progress)} aria-valuemin={0} aria-valuemax={100} aria-label={`Uploading ${j.name}`} className="h-px bg-gray-300">
                <div className={clsx('h-px', j.error ? 'bg-danger' : 'bg-ink')} style={{ width: `${j.progress}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * One uploaded file. Name and size sit on the first line; `controls` (pickers, links) get their own line
 * beneath so nothing is squeezed, and `footnote` (a note on the file) sits under those.
 */
export function UploadedFileRow({ name, size, href, onRemove, controls, footnote }: { name: string; size: number; href?: string; onRemove?: () => void; controls?: ReactNode; footnote?: ReactNode }) {
  const kb = size / 1024
  const label = kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(kb))} KB`
  return (
    <li className="grid gap-2 py-3 text-small">
      <div className="flex items-baseline gap-3">
        <span className="min-w-0 flex-1 truncate text-ink" title={name}>
          {href ? (
            <a href={href} target="_blank" rel="noreferrer" className="underline-offset-4 hover:underline">
              {name}
            </a>
          ) : (
            name
          )}
        </span>
        <span className="shrink-0 text-dim tabular-nums">{label}</span>
      </div>
      {(controls || onRemove) && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {controls}
          {onRemove && (
            <Button variant="danger-link" size="sm" arrow={false} onClick={onRemove} className="ml-auto">
              Remove
            </Button>
          )}
        </div>
      )}
      {footnote}
    </li>
  )
}
